<template>
  <li class="expense-list-entry">
    <div @click="$router.push({ name: 'expense', params: { expenseId: expense.id } })">
      <div>
        <span class="expense-list-entry__amount">{{ amountHumanReadable }}</span>
        <span v-if="hasSupplier" class="expense-list-entry__supplier-name">
          &nbsp;/&nbsp;{{ supplierName }}
        </span>
      </div>
      <div class="expense-list-entry__invoice-date">
        <q-icon name="calendar_today" color="grey-6" />
        <span>&nbsp;{{ $t('invoice_date') }}&nbsp;{{ invoiceDateHumanReadable }}</span>
      </div>
    </div>
    <div @click="$router.push({ name: 'expense', params: { expenseId: expense.id } })">
      <div class="expense-list-entry__payment">
        <div>
          <span v-if="hasDueDate">{{ $t('expense_due_to', { date: dueDateHumanReadable }) }}</span>
          <span v-else>{{ $t('expense_no_due_date') }}</span>
        </div>
        <div v-if="isPaid">
          <q-icon
            name="check"
            color="white"
            class="bg-green-4 circle"
            style="padding: 1px"
            size="12px"
          />&nbsp;{{ $t('expense_paid_at_line', { date: paymentDateHumanReadable })}}
        </div>
        <div v-else>
          <q-icon
            name="clear"
            color="white"
            class="bg-red-4 circle"
            style="padding: 1px"
            size="12px"
          />&nbsp;{{ $t('expense_not_yet_paid') }}
        </div>
      </div>
    </div>

    <div>
      <q-chip
        v-if="isCommitted"
        dense
        color="positive"
        text-color="white"
        icon="lock"
        class="gt-sm"
      >
        {{ $t('committed') }}
      </q-chip>

      <q-icon
        name="lock"
        class="lt-md text-grey-8"
      />
    </div>

    <div class="expense-list-entry__action-btn">
      <q-btn icon="more_vert" color="grey-7" flat round dense>
        <q-popover>
          <q-list link class="no-border">
            <q-item
              v-close-overlay
              @click.native="handleDownload"
            >
              <q-item-side icon="cloud_download"/>
              <q-item-main :label="$t('download')"/>
            </q-item>
            <q-item
              v-if="!isCommitted"
              v-close-overlay
              @click.native="handleCommit"
            >
              <q-item-side icon="lock"/>
              <q-item-main :label="$t('commit')"/>
            </q-item>
            <q-item
              v-if="!isCommitted"
              v-close-overlay
              @click.native="handleDelete"
            >
              <q-item-side icon="delete"/>
              <q-item-main :label="$t('delete')"/>
            </q-item>
          </q-list>
        </q-popover>
      </q-btn>
    </div>

    <div class="expense-list-entry__view-btn">
      <q-btn
          :to="{ name: 'expense', params: { expenseId: this.expense.id, teamId: activeTeam.id } }"
          icon="chevron_right"
          class="q-pa-xs"
          color="grey-8"
          flat
        />
    </div>
  </li>
</template>

<script>
  import { mapActions, mapGetters } from 'vuex'
  import { DocumentsClient } from 'src/api/Documents'
  import FileSaver from 'file-saver'
  import { date } from 'quasar'
  import { formatMoney } from 'src/utils/format'
  import { getCustomerName } from 'src/utils/searchResultParser'
  import parseDate from 'date-fns/parse'

  export default {
    props: {
      expense: {
        type: Object,
        required: true
      }
    },
    computed: {
      ...mapGetters(['activeTeam']),
      hasSupplier () {
        return Object.prototype.hasOwnProperty.call(this.expense, 'supplier')
      },
      hasDueDate () {
        return this.expense.due_date
      },
      supplier () {
        if (!this.hasSupplier) {
          return null
        }

        return this.expense.supplier.data
      },
      supplierName () {
        if (!this.hasSupplier) {
          return ''
        }

        return getCustomerName(this.supplier)
      },
      amountHumanReadable () {
        return formatMoney(this.expense.total_net, this.expense.currency)
      },
      invoiceDateHumanReadable () {
        if (this.expense.invoice_date === null) {
          return ''
        }

        return date.formatDate(parseDate(this.expense.invoice_date.date), this.$t('date_format'))
      },
      dueDateHumanReadable () {
        if (this.expense.due_date === null) {
          return ''
        }

        return date.formatDate(parseDate(this.expense.due_date.date), this.$t('date_format'))
      },
      paymentDateHumanReadable () {
        if (this.expense.last_payment_at === null) {
          return ''
        }

        return date.formatDate(parseDate(this.expense.last_payment_at.date), this.$t('date_format'))
      },
      isPaid () {
        return ['paid', 'paid_cash_discount'].includes(this.expense.status)
      },
      statusText () {
        if (this.expense.paid_at) {
          return this.$t('paid')
        } else {
          return this.$t('open')
        }
      },
      isCommitted () {
        return this.expense.committed_at !== null
      }
    },
    methods: {
      ...mapActions(['commitExpense', 'deleteExpense']),
      handleViewExpense () {
        this.$router.push({ name: 'expense', params: { expenseId: this.expense.id } })
      },
      handleDownload () {
        const document = this.expense.document

        new DocumentsClient()
          .file(document.id)
          .then(response => {
            const blob = new Blob([response.data], { type: document.mime_type })
            FileSaver.saveAs(blob, document.original_name ? document.original_name : document.uuid + document.mime_type.split('/').slice(-1)[0])
          })
      },
      handleCommit () {
        this.commitExpense({ expenseId: this.expense.id })
      },
      handleDelete () {
        this.deleteExpense({ expenseId: this.expense.id })
          .then(() => this.$emit('deleted-expense', this.expense.id))
      }
    }
  }
</script>

<style lang="stylus">
  @import '~variables'

  .expense-list-entry
    display grid
    grid-template-columns 1fr 1fr min-content min-content min-content
    grid-column-gap 1rem
    align-items center
    width 100%
    padding .5rem .25rem .5rem .5rem
    font-size .875rem
    cursor pointer
    user-select none
    &:hover
      background-color $grey-1
    @media(min-width $breakpoint-md-min)
      padding .75rem .5rem .75rem 1.5rem
      grid-template-columns 3fr 3fr 1fr min-content min-content

    &:not(:first-child)
      border-top 1px solid $border-color

    .expense-list-entry__amount
      color $grey-10
      font-weight bold

    .expense-list-entry__supplier-name
      color $grey-8

    .expense-list-entry__invoice-date
      margin-top .25rem
      display flex
      align-items center
      color $grey-7
      @media(min-width $breakpoint-md-min)
        margin-top .5rem

    .expense-list-entry__payment
      > div:nth-child(2)
        margin-top .25rem
        display flex
        align-items center
        color $grey-7
        @media(min-width $breakpoint-md-min)
          margin-top .5rem

    .expense-list-entry__view-btn
      display none
      @media(min-width $breakpoint-md-min)
        display inline
</style>
